import { supabase } from '@/lib/supabase';

export const SUPPORT_ATTACHMENT_BUCKET = 'support-attachments';

const MIME_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/pjpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/avif': 'avif',
  'image/svg+xml': 'svg',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
};

const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif', 'svg', 'heic', 'heif'];

const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/webp', 'image/bmp', 'image/avif'];

/** Lấy phần mở rộng tin cậy: ưu tiên tên file, nếu thiếu thì suy ra từ MIME type. */
export function getFileExtension(file: File): string {
  const raw = (file.name.split('.').pop() || '').toLowerCase().trim();
  if (raw && raw.length <= 5 && /^[a-z0-9]+$/.test(raw)) return raw;
  return MIME_EXTENSION[file.type.toLowerCase()] || 'bin';
}

/** Kiểm tra file có phải ảnh không (dựa cả MIME type lẫn phần mở rộng). */
export function isSupportedImage(file: File): boolean {
  if (file.type.toLowerCase().startsWith('image/')) return true;
  return SUPPORTED_IMAGE_EXTENSIONS.includes(getFileExtension(file));
}

/** Nhận biết URL là ảnh hay không dựa trên đuôi file (bỏ qua query/hash). */
export function isImageAttachmentUrl(ref: string | null): boolean {
  if (!ref) return false;
  const clean = ref.split('?')[0].split('#')[0].toLowerCase();
  return /\.(jpe?g|png|gif|webp|svg|bmp|avif|heic|heif)$/.test(clean);
}

function sanitizeBaseName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  const clean = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50)
    .toLowerCase();
  return clean || 'file';
}

/** Nén ảnh phía trình duyệt để upload ổn định hơn trên mạng yếu. */
export async function compressImage(file: File, maxDimension = 1600, quality = 0.82): Promise<File> {
  if (!COMPRESSIBLE_TYPES.includes(file.type.toLowerCase())) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], `${sanitizeBaseName(file.name)}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

export interface UploadedAttachment {
  url: string;
  name: string;
  path: string;
}

/**
 * Upload file đính kèm lên bucket hỗ trợ với cơ chế chống lỗi "lúc được lúc không":
 * - Nén ảnh trước khi upload (giảm dung lượng, mạng yếu vẫn lên được).
 * - Bảo đảm token còn hiệu lực (tránh lỗi 401 do token hết hạn giữa phiên).
 * - Tên file luôn có phần mở rộng đúng để hiển thị ảnh chuẩn.
 * - Tự động thử lại tối đa 3 lần khi lỗi tạm thời.
 */
export async function uploadSupportAttachment(file: File, folder: string): Promise<UploadedAttachment> {
  const prepared = await compressImage(file);
  const ext = getFileExtension(prepared);
  const base = sanitizeBaseName(prepared.name);
  let lastError: unknown = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const path = `${folder}/${unique}-${base}.${ext}`;
    try {
      await supabase.auth.getSession();
      const { error } = await supabase.storage
        .from(SUPPORT_ATTACHMENT_BUCKET)
        .upload(path, prepared, { contentType: prepared.type || `image/${ext}`, upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from(SUPPORT_ATTACHMENT_BUCKET).getPublicUrl(path);
      return { url: data.publicUrl, name: file.name || `${base}.${ext}`, path };
    } catch (e) {
      lastError = e;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }

  throw new Error(lastError instanceof Error ? lastError.message : 'Không thể tải ảnh lên. Vui lòng thử lại.');
}